import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { PageTemplate } from '../../layout/PageTemplate/PageTemplate';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Chart } from 'primereact/chart';
import { KpiCard } from '../../components/KpiCard/KpiCard';
import { PrimeIcons } from 'primereact/api';
import './BusinessAnalytics.scss';

import { getBusinessChartOptions, pieAggregatedData, themeBarChartByHue, themeLineChart } from '../../utils/chart-utils';
import { Capacitor } from '@capacitor/core';
import useSWR from 'swr';
import { BusinessMetrics } from '../../types/business-metrics';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { useClient } from '../../hooks/useClient';
import { addDays, subDays, subMonths, startOfMonth, endOfMonth, startOfYear, format } from 'date-fns';
import { useLocaleContext } from '../../context/LocaleContext';
import { MetricsUserTable } from '../../components/MetricsUserTable/MetricsUserTable';
import { Skeleton } from 'primereact/skeleton';
import { CardEmptyState } from './components/CardEmptyState';
import { Organization } from '../../types/organization';
import { getRequestUrl } from '../../utils/urlUtils';
import { BranchesSelect } from '../../components/BranchesSelect/BranchesSelect';
import { ReservationTypeSelect } from '../../components/ReservationTypeSelect/ReservationTypeSelect';
import { RESERVATION_COLORS, useReservationTypes } from '../../hooks/AppData/useReservationTypes';
import { fetchWithCache } from '../../utils/cacheUtils';
import { Dropdown } from 'primereact/dropdown';
import { ReservationTypeOccupancy } from '../../components/ReservationTypeOccupancy/ReservationTypeOccupancy';
import { ALL_SELECT_OPTION, getClientTypeOptions } from '../../utils/selectOptions';
import { TabPanel, TabView } from 'primereact/tabview';
import { AdaptiveSelect } from '../../components/AdaptiveSelect/AdaptiveSelect';
import { UserDisplay } from '../../components/UserDisplay/UserDisplay';
import { capitalize } from '../../utils/textUtils';
import { useThemeContext } from '../../context/ThemeContext';

// 15 minutes
const CACHE_TTL = 15 * 60 * 1000;

// Definimos las opciones de periodos predefinidos
const dateRangeOptions = [
  { label: 'Última semana', value: 'last-week' },
  { label: 'Últimas 2 semanas', value: 'last-2-weeks' },
  { label: 'Último mes', value: 'last-month' },
  { label: 'Últimos 3 meses', value: 'last-3-months' },
  { label: 'Últimos 6 meses', value: 'last-6-months' },
  { label: 'Mes actual', value: 'current-month' },
  { label: 'Mes anterior', value: 'previous-month' },
  { label: 'Año actual', value: 'current-year' },
  { label: 'Rango personalizado', value: 'custom' },
];

// Función para calcular el rango de fechas según la opción seleccionada
const getDateRangeFromOption = (option: string): { startDate: Date; endDate: Date } => {
  const today = new Date();

  switch (option) {
    case 'last-week':
      return {
        startDate: subDays(today, 6),
        endDate: today,
      };
    case 'last-2-weeks':
      return {
        startDate: subDays(today, 13),
        endDate: today,
      };
    case 'last-month':
      return {
        startDate: subDays(subMonths(today, 1), 1),
        endDate: today,
      };
    case 'last-3-months':
      return {
        startDate: subDays(subMonths(today, 3), 1),
        endDate: today,
      };
    case 'last-6-months':
      return {
        startDate: subDays(subMonths(today, 6), 1),
        endDate: today,
      };
    case 'current-month':
      return {
        startDate: startOfMonth(today),
        endDate: endOfMonth(today),
      };
    case 'previous-month':
      return {
        startDate: startOfMonth(subMonths(today, 1)),
        endDate: endOfMonth(subMonths(today, 1)),
      };
    // Ends today, not on 31 December, so the charts show no empty future months.
    case 'current-year':
      return {
        startDate: startOfYear(today),
        endDate: today,
      };
    default:
      return {
        startDate: subDays(today, 6),
        endDate: today,
      };
  }
};

// The backend refuses a period of more than 366 days (inclusive), so the picker stops one day earlier than 366 days after the start.
const MAX_RANGE_DAYS_AFTER_START = 365;
const PERIOD_ERROR_MESSAGES = {
  summary: 'Error al cargar las métricas',
  400: 'El periodo no puede superar 366 días.',
};

const getUrl = (
  organization: Organization,
  dateRange: { startDate: Date; endDate: Date },
  branchId: string | undefined,
  reservationTypeName: string | undefined,
  clientType: string | undefined,
) => {
  if (!dateRange.startDate || !dateRange.endDate) return null;
  return getRequestUrl({
    url: `/organizations/${organization.id}/business-analytics`,
    stringParams: {
      startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
      endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
      reservationTypeName: reservationTypeName ?? undefined,
      branchId: branchId ?? undefined,
      clientType,
    },
  });
};

const checkEmptyStates = (data: BusinessMetrics | undefined) => {
  if (!data) return undefined;

  const isMostFrequentParticipantsEmpty = data?.mostFrequentParticipants.length === 0;
  const isMostAbsentParticipantsEmpty = data?.mostAbsentParticipants.length === 0;
  const isUsersWithMostCancellationsEmpty = data?.usersWithMostCancellations.length === 0;
  const isUserCancellationsByReasonEmpty = data?.userCancellationsByReason.length === 0;
  const isUserCancellationsByCanceledByEmpty = data?.userCancellationsByCanceledBy.length === 0;
  const isCancellationsWithin24HoursEmpty =
    !data?.cancellationsWithin24HoursTimeSeries ||
    data.cancellationsWithin24HoursTimeSeries.datasets[0].data.every((value) => value === 0);
  const isUsersWithMostCancellationsWithin24HoursEmpty =
    !data?.usersWithMostCancellationsWithin24Hours || data.usersWithMostCancellationsWithin24Hours.length === 0;
  const isGymActivationsEmpty = data?.gymActivations.length === 0;
  const isGymDeactivationsEmpty = data?.gymDeactivations.length === 0;

  return {
    isMostFrequentParticipantsEmpty,
    isMostAbsentParticipantsEmpty,
    isUsersWithMostCancellationsEmpty,
    isUserCancellationsByReasonEmpty,
    isUserCancellationsByCanceledByEmpty,
    isCancellationsWithin24HoursEmpty,
    isUsersWithMostCancellationsWithin24HoursEmpty,
    isGymActivationsEmpty,
    isGymDeactivationsEmpty,
  };
};

export const BusinessAnalytics = () => {
  const { get } = useClient();
  const { locale } = useLocaleContext();
  const { organization, organizationLoading } = useOrganizationContext();

  // Estado para el selector de periodo predefinido
  const [selectedDateOption, setSelectedDateOption] = useState<string>('last-week');

  // Estado para el rango de fechas (personalizado o calculado)
  const [dateRange, setDateRange] = useState<{ startDate: Date; endDate: Date }>(getDateRangeFromOption('last-week'));

  // Estado para controlar cuando se muestra el selector de rango personalizado
  const [showCustomRange, setShowCustomRange] = useState<boolean>(false);

  const [selectedBranchId, setSelectedBranchId] = useState<string>();
  const [selectedReservationTypeName, setSelectedReservationTypeName] = useState<string>();
  const { withVisitTypeSuffix, uniqueReservationTypes } = useReservationTypes();
  const { theme } = useThemeContext();
  // The attendance rankings count only the visit types when no type is picked (dashboard-backend#897).
  const rankingTitle = (title: string) => (selectedReservationTypeName ? title : withVisitTypeSuffix(title));
  const [selectedClientType, setSelectedClientType] = useState<string>();

  // Actualizar el rango de fechas cuando cambia la opción seleccionada
  useEffect(() => {
    if (selectedDateOption !== 'custom') {
      setDateRange(getDateRangeFromOption(selectedDateOption));
      setShowCustomRange(false);
    } else {
      setShowCustomRange(true);
    }
  }, [selectedDateOption]);

  const fetcher = useCallback(
    (url: string): Promise<BusinessMetrics> =>
      fetchWithCache(url, () => get(url, { errorMessages: PERIOD_ERROR_MESSAGES }), CACHE_TTL),
    [get],
  );

  const { data, isLoading } = useSWR(
    organization ? getUrl(organization, dateRange, selectedBranchId, selectedReservationTypeName, selectedClientType) : null,
    fetcher,
  );
  const loading = organizationLoading || isLoading;

  const emptyStates = checkEmptyStates(data);
  const {
    isMostFrequentParticipantsEmpty,
    isMostAbsentParticipantsEmpty,
    isUsersWithMostCancellationsEmpty,
    isUserCancellationsByReasonEmpty,
    isUserCancellationsByCanceledByEmpty,
    isCancellationsWithin24HoursEmpty,
    isUsersWithMostCancellationsWithin24HoursEmpty,
    isGymActivationsEmpty,
    isGymDeactivationsEmpty,
  } = emptyStates ?? {};

  // The options read CSS variables when they are built: build them again when the theme changes.
  /* eslint-disable react-hooks/exhaustive-deps */
  const lineChartOptions = useMemo(() => getBusinessChartOptions(), [theme]);
  const barChartOptions = useMemo(() => getBusinessChartOptions(), [theme]);
  const currencyChartOptions = useMemo(() => getBusinessChartOptions({ valueSuffix: '€' }), [theme]);
  const largeValueChartOptions = useMemo(() => getBusinessChartOptions({ largeValueChart: true }), [theme]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const clientTypeOptions = useMemo(() => {
    return getClientTypeOptions(organization?.config?.clientTypes, true);
  }, [organization]);

  /* eslint-disable react-hooks/exhaustive-deps -- theme: the pie colors are read from CSS variables when built */
  const cancellationReasonsPieData = useMemo(
    () => (data?.userCancellationsByReason ? pieAggregatedData(data.userCancellationsByReason) : null),
    [data, theme],
  );
  const cancellationByCanceledByPieData = useMemo(
    () => (data?.userCancellationsByCanceledBy ? pieAggregatedData(data.userCancellationsByCanceledBy) : null),
    [data, theme],
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  // The backend still sends fixed series colors for old app bundles: the page paints the series with theme colors instead.
  // A reservation type takes the hue of its calendar cards; a type that is not in the list takes the hue of its position.
  /* eslint-disable react-hooks/exhaustive-deps -- theme: the series colors are read from CSS variables when built */
  const charts = useMemo(() => {
    if (!data) return undefined;
    const hueOfType = (label: string | undefined, index: number) =>
      uniqueReservationTypes.find((type) => capitalize(type.name) === label)?.color ??
      RESERVATION_COLORS[index % RESERVATION_COLORS.length];
    return {
      reservationsByType: themeBarChartByHue(data.totalReservationsByTypeAndDate, hueOfType),
      income: themeLineChart(data.sumPricesGroupedByDay, 'accent'),
      attendances: themeLineChart(data.generalActivity.attendanceTimeSeries, 'success'),
      absences: themeLineChart(data.generalActivity.absenceTimeSeries, 'warning'),
      cancellations: themeLineChart(data.generalActivity.cancellationsTimeSeries, 'danger'),
      cancellationsWithin24Hours: themeLineChart(data.cancellationsWithin24HoursTimeSeries, 'danger'),
      newUsers: themeLineChart(data.newUsersByDate, 'accent'),
      reactivations: themeLineChart(data.reactivationsByDate, 'success'),
      deactivations: themeLineChart(data.deactivationsByDate, 'danger'),
      gymActivations: themeLineChart(data.gymActivationsByDate, 'success'),
      gymDeactivations: themeLineChart(data.gymDeactivationsByDate, 'danger'),
      activeUsers: themeLineChart(data.activeUsersByDate, 'accent'),
    };
  }, [data, theme, uniqueReservationTypes]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Función para manejar el cambio en el rango de fechas personalizado
  const handleCustomRangeChange = (e: any) => {
    if (Array.isArray(e.value) && e.value.length === 2) {
      setDateRange({
        startDate: e.value[0],
        endDate: e.value[1],
      });
    }
  };

  return (
    <PageTemplate className='business-analytics-page' title='Métricas de empresa'>
      <div className='filters-container'>
        <Dropdown
          value={selectedDateOption}
          options={dateRangeOptions}
          onChange={(e) => setSelectedDateOption(e.value)}
          placeholder='Seleccionar periodo'
          className='date-range-selector'
          disabled={loading}
        />

        {showCustomRange && (
          <Calendar
            value={[dateRange.startDate, dateRange.endDate]}
            onChange={handleCustomRangeChange}
            selectionMode='range'
            showIcon
            minDate={new Date(organization?.config?.minMetricsDate ?? '2018-01-01')}
            maxDate={
              dateRange.startDate && !dateRange.endDate ? addDays(dateRange.startDate, MAX_RANGE_DAYS_AFTER_START) : undefined
            }
            className='custom-range-picker'
            locale={locale}
            touchUI={Capacitor.isNativePlatform() || window.matchMedia('(hover: none)').matches}
            disabled={loading}
            placeholder='Seleccionar rango'
            readOnlyInput
            dateFormat='dd/mm/yy'
          />
        )}

        <BranchesSelect
          selectAllOption
          id='branch-filter'
          className='branch-filter'
          placeholder='Sucursales'
          isSingle
          onlyAuthUserBranches
          value={selectedBranchId}
          onChange={(branch) => setSelectedBranchId(branch?.id)}
        />
      </div>
      <TabView>
        <TabPanel header='Reservas'>
          <div className='filters-container'>
            <ReservationTypeSelect
              selectAllOption
              id='reservation-type-filter'
              className='reservation-type-filter'
              placeholder='Tipos de reserva'
              value={selectedReservationTypeName}
              onChange={(reservationTypeName) => setSelectedReservationTypeName(reservationTypeName)}
            />
          </div>

          <div className='kpi-container'>
            {!loading ? (
              <>
                <KpiCard
                  title='Asistencias'
                  value={data?.generalActivity.monthAttendances}
                  previousPercentage={data?.generalActivity.percentageDiffAttendances}
                  icon={PrimeIcons.CHECK}
                  iconColor='var(--success)'
                  iconBkgColor='var(--success-chip)'
                  iconBorderColor='var(--success-chip-border)'
                />
                <KpiCard
                  title='Cancelaciones'
                  value={data?.generalActivity.monthCancellations}
                  previousPercentage={data?.generalActivity.percentageDiffCancellations}
                  icon={PrimeIcons.TIMES}
                  iconColor='var(--danger)'
                  iconBkgColor='var(--danger-chip)'
                  iconBorderColor='var(--danger-chip-border)'
                />
                <KpiCard
                  title='Cancelaciones 24h antes'
                  value={data?.totalCancellationsWithin24Hours}
                  previousPercentage={data?.totalCancellationsWithin24HoursDiff}
                  icon={PrimeIcons.CLOCK}
                  iconColor='var(--danger)'
                  iconBkgColor='var(--danger-chip)'
                  iconBorderColor='var(--danger-chip-border)'
                  tooltip='Cancelaciones realizadas en las 24 horas previas a la hora de inicio de la reserva.'
                />
                <KpiCard
                  title='Ausencias'
                  value={data?.generalActivity.monthAbsences}
                  previousPercentage={data?.generalActivity.percentageDiffAbsences}
                  icon={PrimeIcons.USER_MINUS}
                  iconColor='var(--warning)'
                  iconBkgColor='var(--warning-chip)'
                  iconBorderColor='var(--warning-chip-border)'
                />
                <KpiCard
                  title='Nivel de ocupación'
                  value={`${data?.averageOccupancy ?? 0}%`}
                  previousPercentage={data?.averageOccupancyDiff}
                  icon={'pi pi-gauge'}
                  iconColor='var(--success)'
                  iconBkgColor='var(--success-chip)'
                  iconBorderColor='var(--success-chip-border)'
                  tooltip='Promedio de cuántas personas asisten a las reservas de grupo, en comparación con el total de plazas disponibles.'
                />
                <KpiCard
                  title='Ingresos'
                  value={`${data?.totalIncome ?? 0}€`}
                  previousPercentage={data?.totalIncomeDiff}
                  icon={'pi pi-euro'}
                  iconColor='var(--accent)'
                  iconBkgColor='var(--accent-chip)'
                  iconBorderColor='var(--accent-chip-border)'
                  tooltip='El total de ingresos generado por las reservas con precio.'
                />
              </>
            ) : (
              <>
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </>
            )}
          </div>

          <div className='charts-container'>
            {loading ? (
              <>
                <Skeleton className='full-width-chart' />
                <Skeleton className='full-width-chart' />
                <Skeleton className='small-chart' />
                <Skeleton className='small-chart' />
                <Skeleton className='large-chart' />
                <Skeleton className='small-chart' />
                <Skeleton className='large-chart' />
                <Skeleton className='small-chart' />
                <Skeleton className='large-chart' />
                <Skeleton className='small-chart' />
                <Skeleton className='large-chart' />
                <Skeleton className='small-chart' />
                <Skeleton />
                <Skeleton />
              </>
            ) : (
              <>
                <Card title='Reservas' className='chart-card full-width-chart'>
                  <Chart type='bar' data={charts?.reservationsByType} options={barChartOptions} />
                </Card>

                <Card title='Ingresos' className='chart-card full-width-chart'>
                  <Chart type='line' data={charts?.income} options={currencyChartOptions} />
                </Card>

                <ReservationTypeOccupancy occupancyRanking={data?.occupancyRanking ?? []} />

                <Card title='Asistencias' className='chart-card large-chart'>
                  <Chart type='line' data={charts?.attendances} options={lineChartOptions} />
                </Card>
                <MetricsUserTable
                  className='chart-card small-chart'
                  title={rankingTitle('Clientes con más asistencias')}
                  data={data?.mostFrequentParticipants ?? []}
                  isEmpty={isMostFrequentParticipantsEmpty}
                  emptyStateText='No hay asistencias'
                />
                <Card title='Ausencias' className='chart-card large-chart'>
                  <Chart type='line' data={charts?.absences} options={lineChartOptions} />
                </Card>
                <MetricsUserTable
                  className='chart-card small-chart'
                  title={rankingTitle('Clientes con más ausencias')}
                  data={data?.mostAbsentParticipants ?? []}
                  isEmpty={isMostAbsentParticipantsEmpty}
                  emptyStateText='No hay ausencias'
                />
                <Card title='Cancelaciones' className='chart-card large-chart'>
                  <Chart type='line' data={charts?.cancellations} options={lineChartOptions} />
                </Card>
                <MetricsUserTable
                  className='chart-card small-chart'
                  title='Clientes con más cancelaciones'
                  data={data?.usersWithMostCancellations ?? []}
                  isEmpty={isUsersWithMostCancellationsEmpty}
                  emptyStateText='No hay cancelaciones'
                />

                <Card title='Cancelaciones 24h antes' className='chart-card large-chart'>
                  {isCancellationsWithin24HoursEmpty ? (
                    <CardEmptyState text='No hay cancelaciones dentro de las 24 horas previas' />
                  ) : (
                    <Chart type='line' data={charts?.cancellationsWithin24Hours} options={lineChartOptions} />
                  )}
                </Card>
                <MetricsUserTable
                  className='chart-card small-chart'
                  title='Clientes con más cancelaciones 24h antes'
                  data={data?.usersWithMostCancellationsWithin24Hours ?? []}
                  isEmpty={isUsersWithMostCancellationsWithin24HoursEmpty}
                  emptyStateText='No hay cancelaciones dentro de las 24 horas previas'
                />

                <Card title='Motivos de cancelación' className='chart-card'>
                  {isUserCancellationsByReasonEmpty ? (
                    <CardEmptyState text='No hay cancelaciones' />
                  ) : (
                    <Chart
                      type='pie'
                      data={cancellationReasonsPieData?.chartData}
                      options={cancellationReasonsPieData?.chartOptions}
                    />
                  )}
                </Card>

                <Card title='Origen de las cancelaciones' className='chart-card'>
                  {isUserCancellationsByCanceledByEmpty ? (
                    <CardEmptyState text='No hay cancelaciones' />
                  ) : (
                    <Chart
                      type='pie'
                      data={cancellationByCanceledByPieData?.chartData}
                      options={cancellationByCanceledByPieData?.chartOptions}
                    />
                  )}
                </Card>
              </>
            )}
          </div>
        </TabPanel>
        <TabPanel header='Clientes'>
          <div className='filters-container'>
            <AdaptiveSelect
              id='client-type-filter'
              className='client-type-filter'
              placeholder='Tipos de cliente'
              value={selectedClientType}
              options={clientTypeOptions}
              onChange={(value) => {
                if (value === ALL_SELECT_OPTION) {
                  setSelectedClientType(undefined);
                  return;
                }
                setSelectedClientType(value);
              }}
              isMultiple={false}
              itemTemplate={(option) => <span>{option?.label}</span>}
            />
          </div>
          <div className='kpi-container'>
            {!loading ? (
              <>
                <KpiCard
                  title='Nuevos clientes'
                  value={data?.totalNewUsers}
                  previousPercentage={data?.totalNewUsersDiff}
                  icon={PrimeIcons.USER_PLUS}
                  iconColor='var(--info)'
                  iconBkgColor='var(--info-chip)'
                  iconBorderColor='var(--info-chip-border)'
                />

                <KpiCard
                  title='Bajas'
                  value={data?.totalDeactivations}
                  previousPercentage={data?.totalDeactivationsDiff}
                  icon={PrimeIcons.USER_MINUS}
                  iconColor='var(--danger)'
                  iconBkgColor='var(--danger-chip)'
                  iconBorderColor='var(--danger-chip-border)'
                />

                <KpiCard
                  title='Reactivaciones'
                  value={data?.totalReactivations}
                  previousPercentage={data?.totalReactivationsDiff}
                  icon={PrimeIcons.USER_PLUS}
                  iconColor='var(--success)'
                  iconBkgColor='var(--success-chip)'
                  iconBorderColor='var(--success-chip-border)'
                  tooltip='Clientes que estaban de baja y se han vuelto a dar de alta.'
                />

                <KpiCard
                  title='Activaciones de gym'
                  value={data?.totalGymActivations}
                  previousPercentage={data?.totalGymActivationsDiff}
                  icon={'pi pi-trophy'}
                  iconColor='var(--success)'
                  iconBkgColor='var(--success-chip)'
                  iconBorderColor='var(--success-chip-border)'
                />

                <KpiCard
                  title='Desactivaciones de gym'
                  value={data?.totalGymDeactivations}
                  previousPercentage={data?.totalGymDeactivationsDiff}
                  icon='pi pi-trophy'
                  iconColor='var(--danger)'
                  iconBkgColor='var(--danger-chip)'
                  iconBorderColor='var(--danger-chip-border)'
                />
              </>
            ) : (
              <>
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </>
            )}
          </div>
          <div className='charts-container'>
            {loading ? (
              <>
                <Skeleton className='full-width-chart' />
                <Skeleton />
                <Skeleton />
                <Skeleton className='large-chart' />
                <Skeleton className='small-chart' />
                <Skeleton className='large-chart' />
                <Skeleton className='small-chart' />
                <Skeleton className='full-width-chart' />
              </>
            ) : (
              <>
                <Card title='Nuevos clientes' className='chart-card full-width-chart'>
                  <Chart type='line' data={charts?.newUsers} options={lineChartOptions} />
                </Card>

                <Card title='Reactivaciones' className='chart-card'>
                  <Chart type='line' data={charts?.reactivations} options={lineChartOptions} />
                </Card>

                <Card title='Bajas' className='chart-card'>
                  <Chart type='line' data={charts?.deactivations} options={lineChartOptions} />
                </Card>

                <Card title='Activaciones de gym' className='chart-card large-chart'>
                  <Chart type='line' data={charts?.gymActivations} options={lineChartOptions} />
                </Card>
                <MetricsUserTable
                  className='chart-card small-chart'
                  title='Clientes a los que se ha activado el gym'
                  data={data?.gymActivations ?? []}
                  isEmpty={isGymActivationsEmpty}
                  emptyStateText='No hay activaciones de gym'
                  simpleList
                />
                <Card title='Desactivaciones de gym' className='chart-card large-chart'>
                  <Chart type='line' data={charts?.gymDeactivations} options={lineChartOptions} />
                </Card>
                <MetricsUserTable
                  className='chart-card small-chart'
                  title='Clientes a los que se ha desactivado el gym'
                  data={data?.gymDeactivations ?? []}
                  isEmpty={isGymDeactivationsEmpty}
                  emptyStateText='No hay desactivaciones de gym'
                  simpleList
                />

                <Card title='Clientes activos' className='chart-card full-width-chart'>
                  <Chart type='line' data={charts?.activeUsers} options={largeValueChartOptions} />
                </Card>
              </>
            )}
          </div>
        </TabPanel>
        <TabPanel header='Empleados'>
          <div className='charts-container'>
            {loading ? (
              <>
                <Skeleton className='full-width-chart' />
              </>
            ) : (
              <Card title='Horas trabajadas' className='full-width-chart employee-hours-card'>
                {!data?.employeeHoursData?.employeeHoursByReservationType?.length ? (
                  <CardEmptyState text='No hay datos de horas trabajadas' />
                ) : (
                  <div className='employee-hours-list'>
                    {data.employeeHoursData.employeeHoursByReservationType.map((employeeData) => (
                      <div key={employeeData.userId} className='employee-hours-item'>
                        <div className='employee-hours-header'>
                          <UserDisplay
                            name={employeeData.userName}
                            surnames={employeeData.userSurnames}
                            avatarUrl={employeeData.userAvatarUrl}
                          />
                          <span className='employee-hours-total'>{employeeData.totalHours}h</span>
                        </div>
                        <div className='employee-hours-details'>
                          {employeeData.hoursByReservationType.map((item) => (
                            <div key={item.reservationTypeName} className='employee-hours-detail-item'>
                              <span className='reservation-type-name'>{capitalize(item.reservationTypeName)}</span>
                              <span className='reservation-type-hours'>{item.hours}h</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}
          </div>
        </TabPanel>
      </TabView>
    </PageTemplate>
  );
};
